<template>
  <div class="lead-form">
    <p v-if="step < 3" class="form-title">
      {{ formTitle }}
    </p>
    <div class="inner">
      <div v-show="step === 1" class="step">
        <form
          data-vv-scope="form-1"
          novalidate="true"
          @submit.prevent="changeStep"
        >
          <div class="form-group">
            <input
              v-model="form.name"
              v-validate="'required'"
              type="text"
              name="name"
              class="form-control"
              :class="{ 'is-invalid': errors.has('form-1.name') }"
              placeholder="Name"
            />

            <div
              v-if="errors.has('form-1.name')"
              class="invalid-feedback"
              :class="{ 'd-block': errors.has('form-1.name') }"
              v-text="errors.first('form-1.name')"
            />
          </div>

          <div class="form-group">
            <input
              v-model="form.phone"
              v-validate="'required|tel'"
              type="tel"
              name="phone"
              class="form-control"
              :class="{ 'is-invalid': errors.has('form-1.phone') }"
              placeholder="Telephone"
            />

            <div
              v-if="errors.has('form-1.phone')"
              class="invalid-feedback"
              :class="{ 'd-block': errors.has('form-1.phone') }"
              v-text="errors.first('form-1.phone')"
            />
          </div>

          <div class="form-group">
            <input
              v-model="form.email"
              v-validate="'required'"
              type="email"
              name="email"
              class="form-control"
              :class="{ 'is-invalid': errors.has('form-1.email') }"
              placeholder="Email Address"
            />

            <div
              v-if="errors.has('form-1.email')"
              class="invalid-feedback"
              :class="{ 'd-block': errors.has('form-1.email') }"
              v-text="errors.first('form-1.email')"
            />
          </div>

          <button type="submit" class="btn btn-secondary btn-block mt-5">
            Next
          </button>
        </form>
      </div>

      <div v-show="step === 2" class="step">
        <form data-vv-scope="form-2" @submit.prevent="handleSubmit">
          <div class="form-group">
            <input
              v-model="form.postcode"
              v-validate="'required|postcode'"
              type="text"
              name="postcode"
              class="form-control"
              :class="{ 'is-invalid': errors.has('form-2.postcode') }"
              placeholder="Postcode"
            />
            <div
              v-if="errors.has('form-2.postcode')"
              class="invalid-feedback"
              :class="{ 'd-block': errors.has('form-2.postcode') }"
              v-text="errors.first('form-2.postcode')"
            />
          </div>

          <div class="form-group">
            <select
              v-model="form.income"
              v-validate="'required'"
              name="income"
              class="custom-select"
              :class="{ 'is-invalid': errors.has('form-2.income') }"
              placeholder="Postcode"
            >
              <option :value="null" disabled hidden>
                Household Income
              </option>
              <option value="£10,000 or less">
                £10,000 or less
              </option>
              <option value="£10,000 - £20,000">
                £10,000 - £20,000
              </option>
              <option value="£20,000 - £30,000">
                £20,000 - £30,000
              </option>
              <option value="£30,000 - £40,000">
                £30,000 - £40,000
              </option>
              <option value="£40,000 - £50,000">
                £40,000 - £50,000
              </option>
              <option value="£50,000 or more">
                £50,000 or more
              </option>
            </select>
            <div
              v-if="errors.has('form-2.income')"
              class="invalid-feedback"
              :class="{ 'd-block': errors.has('form-2.income') }"
              v-text="errors.first('form-2.income')"
            />
          </div>

          <div class="form-group">
            <div class="row align-items-center toggle">
              <div class="col">
                <p class="label">
                  Are you a homeowner?
                </p>
              </div>
              <div class="col-auto">
                <toggle v-model="form.homeowner" :values="['Yes', 'No']" />
              </div>
            </div>
          </div>

          <button
            type="submit"
            class="btn btn-secondary btn-block mt-5"
            :class="{ loader: sending }"
            :disabled="sending"
          >
            Submit
          </button>

          <div class="text-center">
            <button type="button" class="back" @click="step = step - 1">
              &laquo; Back
            </button>
          </div>
        </form>
      </div>

      <div v-show="step === 3" class="step thank">
        <img
          :src="`${$themeUrl}/static/vectors/icon-thank.svg`"
          class="img-fluid mb-5"
        />
        <p class="thank-title">
          Thank you very much for you enquiry.
        </p>
        <p class="thank-subtitle">
          We’ll be calling you back very soon.
        </p>
      </div>

      <div v-if="error" class="alert alert-danger mt-5 mb-0" v-text="error" />
    </div>
  </div>
</template>

<script>
import SmoothScroll from "smooth-scroll"
import Toggle from "./toggle.vue"

export default {
  components: { Toggle },
  props: {
    formId: {
      type: Number,
      required: true
    },
    formTitle: {
      type: String,
      default: ""
    },
    thankMode: {
      type: Boolean,
      default: false
    },
    redirectTo: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      step: 1,
      error: "",
      sent: "",
      form: {
        income: null,
        homeowner: "Yes"
      },
      sending: false
    }
  },
  mounted() {
    if (this.thankMode) {
      this.step = 3
    }
  },
  methods: {
    scrollTop() {
      if (window.innerWidth > 768) {
        return
      }

      const target = this.$el

      new SmoothScroll().animateScroll(target, null)
    },

    async validate() {
      return this.$validator.validateAll(`form-${this.step}`)
    },

    async changeStep() {
      if (!(await this.validate())) {
        return
      }

      this.step += 1

      this.scrollTop()
    },

    async handleSubmit() {
      if (!(await this.validate())) {
        return
      }

      this.sending = true
      this.error = ""

      const data = new FormData()

      Object.keys(this.form).forEach(key => {
        data.append(key, this.form[key])
      })

      $.ajax({
        url: `/wp-json/contact-form-7/v1/contact-forms/${this.formId}/feedback`,
        method: "POST",
        data,
        processData: false,
        contentType: false,
        success: res => {
          this.sending = false
          this.scrollTop()

          if (res.status === "mail_sent") {
            if (this.redirectTo) {
              window.location.href = this.redirectTo
            } else {
              this.step = 3
            }
          } else {
            this.error = res.message
          }
        },
        error: error => {
          this.sending = false
          this.error = error.responseJSON.data
          this.scrollTop()
        }
      })
    }
  }
}
</script>
